import { UserCode } from 'api3';
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild, computed, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '@app/services/auth.service';
import { EventTrackerService, QiraEventHumanReadable } from '@app/services/event-tracker.service';
import { PrimeNgMessageService } from '@app/services/primeNgMessage.service';
import { ModalService } from '@app/components/modal/modal.service';
import { getCodeStatus } from './device-code-utils';
import { DeviceCodesListComponent } from './device-codes-list/device-code-list.component';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import * as Sentry from '@sentry/angular';
import { Button } from 'primeng/button';
import tippy from 'tippy.js';

@Component({
  selector: 'app-device-login',
  imports: [CommonModule, FormsModule, TranslocoModule, Button, DeviceCodesListComponent],
  templateUrl: './device-login.component.html',
})
export class DeviceLoginComponent implements AfterViewInit, OnDestroy {
  // Modal inputs (optional - used when opened in a modal)
  data = input<any>();
  close = input<(() => void) | undefined>();

  authService = inject(AuthService);
  messageService = inject(PrimeNgMessageService);
  translocoService = inject(TranslocoService);
  modalService = inject(ModalService);
  router = inject(Router);
  eventTrackerService = inject(EventTrackerService);
  private hostElement = inject(ElementRef);

  @ViewChild('newDeviceInput') newDeviceInput!: ElementRef<HTMLInputElement>;

  allCodes = signal<UserCode[]>([]);
  paginatedCodes = signal<UserCode[]>([]);
  codeNote = '';
  highlightedCodeId = signal<string | undefined>(undefined);
  private sortingDelayTimeouts = new Map<string, number>();

  // Pagination
  currentPage = signal(0);
  pageSize = 20;
  totalPages = computed(() => Math.ceil(this.allCodes().length / this.pageSize));

  ngAfterViewInit(): void {
    this._loadCodes().then(() => {
      this._sortCodes();
      this._updateCurrentPage(true);
      this._initializeTippy();
      this.newDeviceInput?.nativeElement?.focus();
    });
  }

  ngOnDestroy(): void {
    this.sortingDelayTimeouts.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
    this.sortingDelayTimeouts.clear();
  }

  canGoNext = computed(() => this.currentPage() < this.totalPages() - 1);
  canGoPrevious = computed(() => this.currentPage() > 0);
  pageInfo = computed(() => {
    const start = this.currentPage() * this.pageSize + 1;
    const end = Math.min((this.currentPage() + 1) * this.pageSize, this.allCodes().length);
    return `${start}-${end} ${this.translocoService.translate('codes.of')} ${this.allCodes().length}`;
  });

  nextPage() {
    if (this.currentPage() < this.totalPages() - 1) {
      this.currentPage.update((page) => page + 1);
      this._updateCurrentPage(true);
    }
  }

  previousPage() {
    if (this.currentPage() > 0) {
      this.currentPage.update((page) => page - 1);
      this._updateCurrentPage(true);
    }
  }

  handleCodeAction(code: UserCode) {
    const status = getCodeStatus(code);
    if (status === 'used') {
      this._logoutCode(code);
    } else if (status === 'active') {
      this._deactivateCode(code);
    } else if (status === 'logged-out') {
      this._deleteCode(code);
    } else {
      this._deleteCode(code);
    }
  }

  async createCode(event: Event) {
    event.preventDefault();
    event.stopPropagation();

    if ((this.authService.currentUser()?.company?.zip?.length ?? 0) < 2) {
      this._showZipRequiredMessage();
      return false;
    }

    try {
      await this.authService.generateCode(this.codeNote || '');
      this.codeNote = '';
      await this._loadCodes();
      this._sortCodes();
      this._updateCurrentPage(true);
      this._initializeTippy();
      this.messageService.showSuccess('codes.created');
    } catch (error) {
      this.authService.handleNotLoggedInError(error);

      console.error('[DeviceLoginComponent] Failed to generate device code:', error);
      Sentry.captureException(error, {
        tags: {
          location: 'DeviceLoginComponent',
          feature: 'DeviceLogin',
          message: 'Failed to generate device code.',
        },
      });
    }

    return false;
  }

  async copyCodeToClipboard(data: { code: string; event: Event }) {
    const { code, event } = data;

    event.stopPropagation();

    // The code displayed is only the last 6 digits, but we need the full code
    const fullCode = this.allCodes()?.find((c) => c.code.endsWith(code))?.code;
    if (fullCode) {
      try {
        await navigator.clipboard.writeText(fullCode);
        this.messageService.showSuccess('codes.copiedToClipboard');
      } catch (err) {
        console.error('Failed to copy code to clipboard:', err);
        this.messageService.showError('codes.copyFailed');

        Sentry.captureException(err, {
          tags: {
            location: 'DeviceLoginComponent',
            feature: 'DeviceLogin',
            message: 'Failed to copy device code to clipboard.',
          },
        });
      }
    } else {
      console.error('[DeviceLoginComponent] Could not find full code for:', code);
      this.messageService.showError('codes.copyFailed');
      const error = new Error(`[DeviceLoginComponent] Could not find full code ${fullCode} in dataSource.`);
      Sentry.captureException(error, {
        tags: {
          location: 'DeviceLoginComponent',
          feature: 'DeviceLogin',
          message: 'Failed to copy device code to clipboard.',
        },
      });
    }
  }

  private async _loadCodes() {
    try {
      const codes = await this.authService.getCodes();
      const mappedCodes = codes.map((code) => ({
        ...code,
        code: code.code.slice(-6),
      }));
      this.allCodes.set(mappedCodes);
    } catch (error) {
      this.authService.handleNotLoggedInError(error);
      this.allCodes.set([]);
      console.error('Failed to load codes:', error);
      Sentry.captureException(error, {
        tags: {
          location: 'DeviceLoginComponent',
          feature: 'DeviceLogin',
          message: 'Failed to load device codes.',
        },
      });
    }
  }

  private _sortCodes() {
    const statusOrder: Record<'active' | 'used' | 'expired' | 'logged-out', number> = {
      active: 1,
      used: 2,
      expired: 3,
      'logged-out': 4,
    };

    this.allCodes.update((codes) => {
      const sortedCodes = [...codes];
      sortedCodes.sort((a, b) => {
        const statusA = getCodeStatus(a);
        const statusB = getCodeStatus(b);
        return statusOrder[statusA] - statusOrder[statusB];
      });
      return sortedCodes;
    });
  }

  /**
   * Updates the code data, highlights the modified item, and sorts after a delay
   * to improve UX by showing the user that the action was successful
   */
  private async _updateCodeWithDelayedSort(codeId: string) {
    const existingTimeout = this.sortingDelayTimeouts.get(codeId);
    if (existingTimeout) {
      window.clearTimeout(existingTimeout);
    }

    // For delayed updates, first highlight without changing position
    this.highlightedCodeId.set(codeId);

    const timeoutId = window.setTimeout(async () => {
      // Check if this was a delete operation by checking if the code has isDeleting flag
      const codeToCheck = this.allCodes().find((c) => c._id === codeId);
      const wasDeleting = codeToCheck && (codeToCheck as any).isDeleting;
      if (wasDeleting) {
        // For delete operations, just remove from local list
        this.allCodes.update((codes) => codes.filter((c) => c._id !== codeId));
      } else {
        await this._loadCodes();
      }

      this._sortCodes();
      this._updateCurrentPage(true);
      this._initializeTippy();

      setTimeout(() => {
        this.highlightedCodeId.set(undefined);
      }, 500);
      this.sortingDelayTimeouts.delete(codeId);
    }, 2500);
    this.sortingDelayTimeouts.set(codeId, timeoutId);
  }

  private _updateCodesList(codeId: string, localUpdate: (code: UserCode) => UserCode) {
    this.allCodes.update((codes) => {
      const codeIndex = codes.findIndex((c) => c._id === codeId);
      if (codeIndex !== -1) {
        const newCodes = [...codes];
        newCodes[codeIndex] = localUpdate(newCodes[codeIndex]);
        return newCodes;
      }
      return codes;
    });
  }

  private _updateCurrentPage(shouldScroll = false) {
    const start = this.currentPage() * this.pageSize;
    const end = start + this.pageSize;

    this.paginatedCodes.set(this.allCodes().slice(start, end));
    this._initializeTippy();
    if (shouldScroll) {
      this._scrollToTop();
    }
  }

  private _scrollToTop() {
    setTimeout(() => {
      if (this.hostElement?.nativeElement) {
        // Find the scrollable parent container and scroll it to top
        const scrollableParent = this.hostElement.nativeElement.closest('.overflow-auto, .overflow-y-auto, [style*="overflow"]') as HTMLElement;
        if (scrollableParent) {
          scrollableParent.scrollTop = 0;
        } else {
          this.hostElement.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    });
  }

  private _initializeTippy() {
    setTimeout(() => {
      tippy('[data-tippy-content]', {});
    });
  }

  private _deactivateCode(code: UserCode) {
    this._showConfirmation('codes.confirmTitle', 'codes.confirmContent', 'codes.confirmPrimaryButton', 'codes.confirmCancelButton', async () => {
      try {
        await this.authService.deactivateCode(code._id);

        // Update local state immediately - deactivated code becomes 'expired'
        this._updateCodesList(code._id, (localCode) => ({
          ...localCode,
          createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
        }));

        this._updateCurrentPage(false);
        this._updateCodeWithDelayedSort(code._id);

        this.eventTrackerService.logEvent(QiraEventHumanReadable.DEVICE_CODE_INVALIDATED_BY_ADMIN, {
          code_id: code._id,
          code_redeemed: false,
        });
        this.messageService.showSuccess('codes.deactivated');
      } catch (error) {
        this.authService.handleNotLoggedInError(error);
        console.error('[DeviceLoginComponent] Failed to deactivate device code:', error);
        Sentry.captureException(error, {
          tags: {
            location: 'DeviceLoginComponent',
            feature: 'DeviceLogin',
            message: 'Failed to deactivate device code.',
          },
        });
      }
    });
  }

  private _logoutCode(code: UserCode) {
    this._showConfirmation(
      'codes.confirmLogoutTitle',
      'codes.confirmLogoutContent',
      'codes.confirmLogoutPrimaryButton',
      'codes.confirmCancelButton',
      async () => {
        try {
          await this.authService.deactivateCode(code._id);

          this._updateCodesList(code._id, (localCode) => ({
            ...localCode,
            isLoggedOut: true,
          }));

          this._updateCurrentPage(false);
          this._updateCodeWithDelayedSort(code._id);

          this.eventTrackerService.logEvent(QiraEventHumanReadable.DEVICE_CODE_INVALIDATED_BY_ADMIN, {
            code_id: code._id,
            code_redeemed: true,
          });
          this.messageService.showSuccess('codes.loggedOut');
        } catch (error) {
          this.authService.handleNotLoggedInError(error);
          console.error('[DeviceLoginComponent] Failed to logout device code:', error);
          Sentry.captureException(error, {
            tags: {
              location: 'DeviceLoginComponent',
              feature: 'DeviceLogin',
              message: 'Failed to logout device code.',
            },
          });
        }
      },
    );
  }

  private _deleteCode(code: UserCode) {
    this._showConfirmation(
      'codes.confirmDeleteTitle',
      'codes.confirmDeleteContent',
      'codes.confirmDeletePrimaryButton',
      'codes.confirmCancelButton',
      async () => {
        try {
          await this.authService.hideCode(code._id);

          // Update local state immediately - mark code as "deleting" for visual feedback
          this._updateCodesList(code._id, (localCode) => ({
            ...localCode,
            isDeleting: true,
            isLoggedOut: true,
          }));

          this._updateCurrentPage(false);
          this._updateCodeWithDelayedSort(code._id);

          this.messageService.showSuccess('codes.deleted');
        } catch (error) {
          this.authService.handleNotLoggedInError(error);
          console.error('[DeviceLoginComponent] Failed to delete device code:', error);
          Sentry.captureException(error, {
            tags: {
              location: 'DeviceLoginComponent',
              feature: 'DeviceLogin',
              message: 'Failed to delete device code.',
            },
          });
        }
      },
    );
  }

  private _showConfirmation(title: string, content: string, primaryButtonText: string, secondaryButtonText: string, onConfirm: () => void) {
    this.modalService.confirm(title, content, primaryButtonText, secondaryButtonText).then((confirmed) => {
      if (confirmed) {
        onConfirm();
      }
    });
  }

  private _showZipRequiredMessage() {
    this._showConfirmation(
      'codes.zipMissingDialog.title',
      'codes.zipMissingDialog.content',
      'codes.zipMissingDialog.primaryButtonText',
      'global.cancel',
      () => {
        setTimeout(() => {
          this.router.navigate(['/profile/me']);
        }, 10);
      },
    );
  }
}
